import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { Box, Flex, Text, VStack, Icon } from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { HiHome, HiPhotograph } from "react-icons/hi";
import { AiOutlineFire } from "react-icons/ai";
import { BiCalendar } from "react-icons/bi";
import { MdRestaurantMenu } from "react-icons/md";
import { FaUser } from "react-icons/fa";
import { HiPlusCircle } from "react-icons/hi";
const NAV_WIDTH = 380;
const BAR_HEIGHT = 78;
const BAR_TOP = 36;
const BAR_RADIUS = 39;
const BAR_BORDER = 1;
const CENTER_BTN = 68;
const CENTER_TOP = -8;
const CENTER_GAP = 118;
const NOTCH_EXTRA = 20;
const NOTCH_START = (NAV_WIDTH - CENTER_GAP) / 2 - NOTCH_EXTRA;
const NOTCH_END = (NAV_WIDTH + CENTER_GAP) / 2 + NOTCH_EXTRA;
const NOTCH_DEPTH = 36;
const NOTCH_SHOULDER_OUTER = 46;
const NOTCH_SHOULDER_INNER = 24;
const NOTCH_CONFIG_DEPTH = NOTCH_DEPTH;
const NOTCH_CONFIG_RADIUS = NOTCH_SHOULDER_INNER;
const COMPONENT_HEIGHT = BAR_TOP + BAR_HEIGHT + 10;
const toggleIconPop = keyframes `
  0% {
    transform: translateY(6px) scale(0.7) rotate(-14deg);
    opacity: 0;
    filter: drop-shadow(0 0 0 rgba(129, 85, 254, 0));
  }
  50% {
    transform: translateY(-2px) scale(1.12) rotate(4deg);
    opacity: 1;
    filter: drop-shadow(0 0 12px rgba(129, 85, 254, 0.55));
  }
  100% {
    transform: translateY(0) scale(1) rotate(0deg);
    opacity: 1;
    filter: drop-shadow(0 0 0 rgba(129, 85, 254, 0));
  }
`;
const toggleLabelSlide = keyframes `
  0% {
    transform: translateY(4px);
    opacity: 0;
    letter-spacing: 0.08em;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
    letter-spacing: 0.01em;
  }
`;
export const BottomNavBar = ({ onNavigate, activeId, showMenu = false, showEvents = false, labels, menuColors, }) => {
    const [offersToggleTick, setOffersToggleTick] = useState(0);
    const [menuToggleTick, setMenuToggleTick] = useState(0);
    useEffect(() => {
        setOffersToggleTick((prev) => prev + 1);
    }, [showEvents]);
    useEffect(() => {
        setMenuToggleTick((prev) => prev + 1);
    }, [showMenu]);
    const activeColor = (menuColors === null || menuColors === void 0 ? void 0 : menuColors.primary) || "#8155FE";
    const textColor = (menuColors === null || menuColors === void 0 ? void 0 : menuColors.text) || "#797979";
    const iconColor = (menuColors === null || menuColors === void 0 ? void 0 : menuColors.text) || "#797979";
    const navBackground = (menuColors === null || menuColors === void 0 ? void 0 : menuColors.bg) || "#232323";
    const navBorder = "#5B5959";
    const reserveButtonBg = activeColor;
    const reserveButtonIconColor = (menuColors === null || menuColors === void 0 ? void 0 : menuColors.text) || "#E9EAED";
    const reserveButtonTextColor = (menuColors === null || menuColors === void 0 ? void 0 : menuColors.text) || "rgba(255,255,255,0.96)";
    const resolvedNotchDepth = Math.min(BAR_HEIGHT - 8, Math.max(8, NOTCH_CONFIG_DEPTH));
    const resolvedNotchRadius = Math.min(CENTER_GAP / 2 - 2, Math.max(8, NOTCH_CONFIG_RADIUS));
    const resolvedNotchShoulderOuter = Math.max(NOTCH_SHOULDER_OUTER, resolvedNotchRadius + 14);
    const resolvedLabels = {
        home: "Home",
        offers: "Offers",
        events: "Events",
        menu: "Menu",
        gallery: "Gallery",
        profile: "Profile",
        reserve: "Reserve",
        ...labels,
    };
    const menuOrGalleryId = showMenu ? "menu" : "gallery";
    const leftItems = [
        {
            id: "home",
            label: resolvedLabels.home,
            icon: _jsx(Icon, { as: HiHome }),
            onClick: onNavigate.home,
        },
        {
            id: "offers",
            label: showEvents ? resolvedLabels.events : resolvedLabels.offers,
            icon: _jsx(Icon, { as: showEvents ? BiCalendar : AiOutlineFire }),
            onClick: onNavigate.offers,
        },
    ];
    const rightItems = [
        {
            id: menuOrGalleryId,
            label: showMenu ? resolvedLabels.menu : resolvedLabels.gallery,
            icon: _jsx(Icon, { as: showMenu ? MdRestaurantMenu : HiPhotograph }),
            onClick: onNavigate.menuOrGallery,
        },
        {
            id: "profile",
            label: resolvedLabels.profile,
            icon: _jsx(Icon, { as: FaUser }),
            onClick: onNavigate.profile,
        },
    ];
    const renderNavItem = (item) => {
        const isActive = item.id === activeId;
        const shouldAnimateOffersToggle = item.id === "offers";
        const shouldAnimateMenuToggle = item.id === menuOrGalleryId;
        const animationTick = shouldAnimateOffersToggle
            ? offersToggleTick
            : shouldAnimateMenuToggle
                ? menuToggleTick
                : 0;
        const shouldAnimate = shouldAnimateOffersToggle || shouldAnimateMenuToggle;
        return (_jsxs(VStack, { spacing: "4px", align: "center", justify: "center", flex: 1, cursor: "pointer", onClick: item.onClick, role: "button", "aria-label": item.label, userSelect: "none", transition: "opacity 0.15s", _active: { opacity: 0.65 }, h: "100%", textAlign: "center", children: [_jsx(Box, { fontSize: "24px", lineHeight: "1", color: isActive ? activeColor : iconColor, opacity: isActive ? 1 : 0.9, transition: "color 0.2s", animation: shouldAnimate ? `${toggleIconPop} 460ms cubic-bezier(0.2, 0.9, 0.2, 1)` : undefined, sx: {
                        "& svg": {
                            width: "24px !important",
                            height: "24px !important",
                        },
                    }, children: item.icon }), _jsx(Text, { fontSize: "10px", fontWeight: 400, color: isActive ? activeColor : textColor, opacity: isActive ? 1 : 0.9, lineHeight: "1", letterSpacing: "0.01em", transition: "color 0.2s", animation: shouldAnimate ? `${toggleLabelSlide} 360ms ease-out` : undefined, children: item.label })] }, shouldAnimate ? `${item.id}-${animationTick}` : item.id));
    };
    return (_jsxs(Box, { position: "relative", w: `min(${NAV_WIDTH}px, calc(100vw - 16px))`, mx: "auto", h: `${COMPONENT_HEIGHT}px`, overflow: "visible", children: [_jsx(Box, { position: "absolute", top: `${CENTER_TOP}px`, left: "50%", transform: "translateX(-50%)", zIndex: 20, children: _jsxs(Box, { as: "button", w: `${CENTER_BTN}px`, h: `${CENTER_BTN}px`, borderRadius: "full", bg: reserveButtonBg, border: "none", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "4px", color: "white", cursor: "pointer", boxShadow: "0 10px 24px rgba(0,0,0,0.35)", transition: "transform 0.15s ease, box-shadow 0.15s ease", onClick: onNavigate.reserve, "aria-label": resolvedLabels.reserve, _hover: {
                        transform: "translateY(-1px)",
                        boxShadow: "0 14px 28px rgba(0,0,0,0.38)",
                    }, _active: { transform: "scale(0.96)" }, _focus: { outline: "none" }, p: "8px", children: [_jsx(Box, { w: "30px", h: "30px", borderRadius: "full", color: reserveButtonIconColor, display: "flex", alignItems: "center", justifyContent: "center", bg: reserveButtonBg, fontSize: "30px", lineHeight: "1", sx: {
                                "& svg": {
                                    width: "30px !important",
                                    height: "30px !important",
                                },
                            }, children: _jsx(Icon, { as: HiPlusCircle }) }), _jsx(Text, { fontSize: "10px", fontWeight: 400, color: reserveButtonTextColor, lineHeight: "1", textAlign: "center", children: resolvedLabels.reserve })] }) }), _jsxs(Box, { position: "absolute", top: `${BAR_TOP}px`, left: 0, right: 0, h: `${BAR_HEIGHT}px`, zIndex: 10, overflow: "visible", bg: "transparent", children: [_jsx(Box, { position: "absolute", inset: 0, pointerEvents: "none", children: _jsx("svg", { width: "100%", height: "100%", viewBox: `0 0 ${NAV_WIDTH} ${BAR_HEIGHT}`, preserveAspectRatio: "none", xmlns: "http://www.w3.org/2000/svg", children: _jsx("path", { d: `
                M ${BAR_RADIUS} ${BAR_HEIGHT}
                H ${NAV_WIDTH - BAR_RADIUS}
                A ${BAR_RADIUS} ${BAR_RADIUS} 0 0 0 ${NAV_WIDTH} ${BAR_HEIGHT - BAR_RADIUS}
                V ${BAR_RADIUS}
                A ${BAR_RADIUS} ${BAR_RADIUS} 0 0 0 ${NAV_WIDTH - BAR_RADIUS} 0
                H ${NOTCH_END}
                C ${NOTCH_END - resolvedNotchShoulderOuter} 0 ${NAV_WIDTH / 2 + resolvedNotchRadius} ${resolvedNotchDepth} ${NAV_WIDTH / 2} ${resolvedNotchDepth}
                C ${NAV_WIDTH / 2 - resolvedNotchRadius} ${resolvedNotchDepth} ${NOTCH_START + resolvedNotchShoulderOuter} 0 ${NOTCH_START} 0
                H ${BAR_RADIUS}
                A ${BAR_RADIUS} ${BAR_RADIUS} 0 0 0 0 ${BAR_RADIUS}
                V ${BAR_HEIGHT - BAR_RADIUS}
                A ${BAR_RADIUS} ${BAR_RADIUS} 0 0 0 ${BAR_RADIUS} ${BAR_HEIGHT}
                Z
              `, fill: navBackground, stroke: navBorder, strokeWidth: BAR_BORDER, style: { filter: "drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25))" } }) }) }), _jsxs(Flex, { position: "absolute", inset: 0, align: "center", h: "100%", children: [_jsx(Flex, { flex: 1, align: "center", justify: "center", h: "100%", pl: 4, gap: 6, children: leftItems.map(renderNavItem) }), _jsx(Box, { w: `${CENTER_GAP}px`, flexShrink: 0 }), _jsx(Flex, { flex: 1, align: "center", justify: "center", h: "100%", pr: 4, gap: 6, children: rightItems.map(renderNavItem) })] })] })] }));
};
export default BottomNavBar;
